import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

// Helper functions
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    return false;
  }
};

const openMaps = (address) => {
  const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

// Small UI components
const Badge = ({ children, className = "" }) => (
  <span className={`inline-flex items-center rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-xs font-medium text-white/90 backdrop-blur ${className}`}>
    {children}
  </span>
);

const IconChevron = ({ open, className = "w-5 h-5" }) => (
  <motion.svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    initial={false}
    animate={{ rotate: open ? 180 : 0 }}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </motion.svg>
);

const IconMap = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
    <path d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3z" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="15" cy="10" r="2" />
  </svg>
);

const IconCopy = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

// Hotel card
const HotelCard = ({ name, address, subtitle = "Tour HQ • Check-in point" }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyToClipboard(`${name}\n${address}`);
    setCopied(ok);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900 via-slate-900/90 to-slate-800 text-white shadow-lg overflow-hidden">
      <div className="flex items-center gap-3 px-4 sm:px-6 py-4">
        <Badge>Hotel</Badge>
        <div className="min-w-0">
          <h2 className="text-lg sm:text-xl font-semibold leading-tight truncate">{name}</h2>
          <p className="text-xs sm:text-sm text-white/60">{subtitle}</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            onClick={() => openMaps(address)}
            className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs hover:bg-white/10 active:scale-[0.98]"
          >
            <IconMap className="w-4 h-4" />
            Maps
          </button>
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs hover:bg-white/10 active:scale-[0.98]"
          >
            <IconCopy className="w-4 h-4" />
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            onClick={() => setOpen(!open)}
            className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs hover:bg-white/10 active:scale-[0.98]"
          >
            <IconChevron open={open} className="w-4 h-4" />
            {open ? "Hide" : "Show"}
          </button>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-4 sm:px-6 pb-4"
          >
            <div className="rounded-xl bg-white/5 p-3 sm:p-4 border border-white/10">
              <p className="text-sm leading-relaxed text-white/85 whitespace-pre-line">
                {address}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Day card
const DayCard = ({ index, expanded, onToggle, title, subtitle, events }) => (
  <motion.div layout className="rounded-2xl border border-slate-200/60 bg-white shadow-sm overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between text-left"
    >
      <div>
        <h3 className="font-semibold text-slate-800">{title}</h3>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
      <IconChevron open={expanded} className="w-5 h-5 text-slate-500" />
    </button>

    <AnimatePresence initial={false}>
      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="px-4 sm:px-5 pb-3 sm:pb-4"
        >
          <ul className="divide-y divide-slate-200/70">
            {events?.map((ev, i) => (
              <li key={i} className="py-2.5 sm:py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
                <span className="font-medium text-slate-700 tracking-tight">{ev.time}</span>
                <span className="text-slate-600">{ev.title}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

// Main page
const ItineraryPage = () => {
  const [expandedDay, setExpandedDay] = useState(null);
  const [days, setDays] = useState([]);

  useEffect(() => {
    const q = query(collection(db, "itineraries"), orderBy("date", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const daysData = [];
      snapshot.forEach((doc) => daysData.push({ id: doc.id, ...doc.data() }));
      setDays(daysData);
    });
    return () => unsubscribe();
  }, []);

  const toggleDay = (i) => setExpandedDay((p) => (p === i ? null : i));

  const hotel = {
    name: "Fairfield Inn and Suites Union",
    address: "1730 US Highway 22 E\\nUnion, NJ 07083, USA",
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">December Tour Itinerary</h1>
          <Badge className="hidden sm:inline-flex">7:00 AM – 9:00 PM</Badge>
        </div>
        <p className="mt-2 text-white max-w-prose">
          Below is our day‑by‑day schedule. Tap a day to expand details. Hotel address is available below without taking over the page.
        </p>
      </div>

      <div className="mb-6 sm:mb-8">
        <HotelCard name={hotel.name} address={hotel.address} />
      </div>

      <div className="space-y-3 sm:space-y-4">
        {days.map((day, i) => (
          <DayCard
            key={day.id}
            index={i}
            expanded={expandedDay === i}
            onToggle={() => toggleDay(i)}
            title={`${day.date} — ${day.day}`}
            subtitle={day?.subtitle}
            events={day.events}
          />
        ))}
      </div>
    </div>
  );
};

export default ItineraryPage;
